import { useContext, useRef, useState } from 'react'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { LayoutContext } from 'providers/LayoutProvider'
import { Eyebrow } from 'components/Eyebrow'
import { Typography } from 'components/Contentful'
import { Button } from 'components/Button'

import classNames from 'classnames'
import Quiz from './Quiz'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import { mainProductConfig } from 'services/contentful/helpers'
import { getSlug } from 'utils/conversions'

function DebtQuizModule({
  title,
  subTitle,
  eyebrow,
  background,
  jsonConfig,
  cta,
  questionGroup1,
  questionGroup2,
  questionGroup3,
  questionGroup4,
  debtQuizId,
}) {
  const { fireAndForget, fireAndForgetPage } = useTriggerEvents()
  const { dispatch } = useContext(AnalyticsContext)
  const { quiz_name } = jsonConfig
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const textContainerRef = useRef(null)
  const backgroundConfig = jsonConfig?.background ?? 'bgDefault'
  const maxScoreValue = jsonConfig?.maxScoreValue ?? 900
  const [startQuiz, setStartQuiz] = useState(false)

  const TriggerNewPageEvent = (question, event) => {
    dispatch({
      type: 'SET_QUIZ_PAGE_NAME',
      payload: {
        quiz_page: `${quiz_name} - Question ${question}`,
      },
    })
    fireAndForgetPage({
      event_type: 'page',
      event_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - Question ${question} - Page Loaded`,
      page_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - Question ${question}`,
      quiz_page: `${quiz_name} - Question ${question}`,
      ...handleTrackAndReactEvent(event, {}),
    })
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }, 0)
  }

  const TriggerResult = (question, quiz_answer, event) => {
    fireAndForget({
      event_type: 'button_click',
      click_id: 'Continue',
      click_position: '0',
      click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | Continue`,
      click_type: 'Button Click',
      event_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - Question ${question} - Page Loaded`,
      page_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - Question ${question}`,
      quiz_answer,
      quiz_page: `${quiz_name} - Question ${question}`,
      nav_link_section: 'Quiz Question',
      track_event: 'page_nav_click',
      ...handleTrackAndReactEvent(event, {}),
    })
  }

  const TriggerResultPage = (result, score, event) => {
    dispatch({
      type: 'SET_QUIZ_PAGE_NAME',
      payload: {
        quiz_page: `${quiz_name} - ${result}`,
        quiz_score: score,
      },
    })
    fireAndForgetPage({
      event_type: 'page',
      event_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - ${result} - Page Loaded`,
      page_name: `${
        mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
      } | ${quiz_name} - ${result}`,
      quiz_page: `${quiz_name} - ${result}`,
      quiz_score: score,
      ...handleTrackAndReactEvent(event, {}),
    })
  }

  const {
    heroVariant,
    backgroundObjectPosition = 'right',
    backgroundObjectPositionMobile = 'bottom',
    titleVariantMobile = 'headingLg',
    titleVariant = 'displayXl',
    subTitleVariantMobile = 'bodyLg',
    subTitleVariant = 'bodyLg',
    showPattern = false,
    textAlign,
  } = jsonConfig

  const b1 = `bg-${getSlug(backgroundObjectPositionMobile)}`
  const b2 = `lg:bg-${getSlug(backgroundObjectPosition)}`

  return (
    <section
      className={
        'relative max-w-full lg:[&_[class*="faq-section"]]:[&_[class*="faq-section"]]:w-[390px] lg:[&_[class*="faq-section"]]:[&_[class*="faq-section"]]:max-h-40'
      }
    >
      {!startQuiz ? (
        <Section
          className={
            'max-lg:data-[hero-variant="debt-consolidation"]:h-[342px] max-md:data-[hero-variant="debt-consolidation"]:h-[345px]'
          }
          data-testid="closer-fullbg-section"
          data-narrow={true}
          data-hero-variant={heroVariant}
          mediaContent={background?.mediaContent}
          middleMediaContent={background?.mobileMediaContent}
          mobileMediaContent={background?.mobileMediaContent}
          backgroundImageAlt={background?.mediaAltText}
          showPattern={showPattern}
          backgroundObjectPositionClass={`${b1} ${b2}`}
          variant={backgroundConfig}
          preloadBackground
          full
        >
          <div
            className={'block justify-center items-center min-h-[750px] md:flex md:min-h-[500px]'}
          >
            <div
              className={classNames(
                'z-[1] relative translate-y-[90%] md:max-w-screen-sm md:p-0 md:[transform:inherit]',
                'group-[data-container-color="blue"]:bg-blue-500 group-[data-container-color="blue"]:rounded-[10px] group-[data-container-color="blue"]:p-6',
                'lg:[transform:none] lg:group-[data-container-color="blue"]:p-10',
                'xl:transform: none',
                'xl:group-[data-container-color="blue"]:p-10',
                `text-${textAlign?.mobile ?? 'center'} lg:text-${textAlign?.desktop ?? 'left'}`
              )}
              ref={textContainerRef}
              data-testid="debt-quiz-text-container"
            >
              {eyebrow && (
                <Eyebrow
                  content={eyebrow?.textContent}
                  fontWeight="bold"
                  component="h1"
                  className={classNames('text-neutral-300 mb-4 pb-0', {
                    [`text-${eyebrow?.position}`]: Boolean(eyebrow?.position),
                  })}
                />
              )}
              {title && (
                <Typography
                  content={title?.textContent}
                  className={classNames(
                    title?.fontColor?.toLowerCase() === 'white' && '!text-white',
                    title?.fontColor?.toLowerCase() === 'blue' && '!text-blue-500',
                    title?.fontColor?.toLowerCase() === 'black' && '!text-neutral-900',
                    //IS THIS A TYPO?
                    title?.fontColor?.toLowerCase() === 'gery' && '!text-neutral-500',
                    `${titleVariantMobile} lg:${titleVariant} font-bold`
                  )}
                />
              )}
              {subTitle && (
                <Typography
                  content={subTitle?.textContent}
                  className={classNames(
                    'my-4',
                    title?.fontColor?.toLowerCase() === 'white' && 'text-white',
                    title?.fontColor?.toLowerCase() === 'blue' && 'text-blue-500',
                    title?.fontColor?.toLowerCase() === 'black' && 'text-neutral-900',
                    //IS THIS A TYPO?
                    title?.fontColor?.toLowerCase() === 'gery' && 'text-neutral-500',
                    `${subTitleVariantMobile} lg:${subTitleVariant}`
                  )}
                />
              )}
              {cta && (
                <div className={'flex justify-center items-center'}>
                  <Button
                    color="primary"
                    variant="contained"
                    data-testid="skinny-form-cta"
                    onClick={(event) => {
                      setStartQuiz(true)
                      TriggerNewPageEvent(1, event)
                    }}
                    className="w-full lg:w-auto"
                    track={{
                      click_id: 'start-quiz',
                      click_text:
                        cta?.textContent?.json?.content?.[0]?.content?.[0]?.value || 'Start Quiz',
                      click_type: 'Button Click',
                      click_position: 1,
                      event_type: 'button_click',
                      nav_link_section: 'Quiz Start',
                    }}
                  >
                    <Typography content={cta?.textContent} variant="bodyLg" fontWeight="bold" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Section>
      ) : (
        <Quiz
          questionGroup1={questionGroup1}
          questionGroup2={questionGroup2}
          questionGroup3={questionGroup3}
          questionGroup4={questionGroup4}
          maxScoreValue={maxScoreValue}
          debtQuizId={debtQuizId}
          TriggerNewPageEvent={TriggerNewPageEvent}
          TriggerResult={TriggerResult}
          TriggerResultPage={TriggerResultPage}
        />
      )}
    </section>
  )
}

const DebtQuiz = ({ content }) => {
  const {
    title,
    subTitle,
    eyebrow,
    background,
    cta,
    questionGroup1,
    questionGroup2,
    questionGroup3,
    questionGroup4,
    configJson: { jsonContent },
  } = useMemoizedContentGetter(content, [
    'title',
    'subTitle',
    'eyebrow',
    'background',
    'cta',
    'questionGroup1',
    'questionGroup2',
    'questionGroup3',
    'questionGroup4',
    'configJson',
  ])
  return (
    <DebtQuizModule
      title={title}
      subTitle={subTitle}
      eyebrow={eyebrow}
      background={background}
      cta={cta}
      questionGroup1={questionGroup1}
      questionGroup2={questionGroup2}
      questionGroup3={questionGroup3}
      questionGroup4={questionGroup4}
      jsonConfig={jsonContent}
      debtQuizId={content?.fields?.sys?.id}
    />
  )
}

export { DebtQuiz, DebtQuizModule }
export default DebtQuiz
